import { useEffect, useState } from "react"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { toast } from "sonner"
import { useIsSuperAdmin } from "./session-context"
import type { AdminRole } from "./session-context"
import { MembersIcon } from "./components/icons"
import { Modal, ModalActions, ModalButton } from "./components/modal"
import { useConfirm } from "./components/confirm-modal"
import { RolesPageSkeleton } from "./components/skeleton"
import { PERMISSION_GROUPS, PERMISSION_LABELS, type Permission } from "@server/permissions"

interface RoleWithDates extends AdminRole {
  createdAt: string
  updatedAt: string
}

function PermissionForm({
  selectedPermissions,
  onToggle,
}: {
  selectedPermissions: string[]
  onToggle: (permission: string) => void
}) {
  return (
    <div className="max-h-[300px] space-y-4 overflow-y-auto">
      {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
        <div key={group}>
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink/60">
            {group}
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {permissions.map((permission) => (
              <label
                key={permission}
                className="flex cursor-pointer items-center gap-2 rounded-lg border border-gold-500/50 px-3 py-2 hover:bg-cream-50"
              >
                <input
                  type="checkbox"
                  checked={selectedPermissions.includes(permission)}
                  onChange={() => onToggle(permission)}
                  className="h-4 w-4 rounded border-gold-500 text-forest-950 focus:ring-forest-700"
                />
                <span className="text-sm text-ink">
                  {PERMISSION_LABELS[permission as Permission]}
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export function RolesPage() {
  const isSuperAdmin = useIsSuperAdmin()
  const [roles, setRoles] = useState<RoleWithDates[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingRole, setEditingRole] = useState<RoleWithDates | null>(null)
  const [form, setForm] = useState({ name: "", permissions: [] as string[] })
  const [saving, setSaving] = useState(false)
  const { confirm, modal } = useConfirm()

  const fetchRoles = async () => {
    try {
      const res = await client.api.admin.roles.$get()
      const body = await res.json()
      if (!res.ok) {
        setError(getErrorMessage(body))
        return
      }
      if ("data" in body) {
        // Fetch full details for each role to get dates
        const fullRoles = await Promise.all(
          body.data.map(async (role: AdminRole) => {
            const detailRes = await client.api.admin.roles[":id"].$get({ param: { id: role.id } })
            const detailBody = await detailRes.json()
            if (detailRes.ok && "data" in detailBody) {
              return detailBody.data
            }
            return { ...role, createdAt: "", updatedAt: "" }
          })
        )
        setRoles(fullRoles)
      }
    } catch {
      setError("Failed to load roles")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void fetchRoles()
  }, [])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      toast.error("Role name is required")
      return
    }
    setSaving(true)
    try {
      const res = await client.api.admin.roles.$post({
        json: { name: form.name, permissions: form.permissions, isSuper: false },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Role created successfully")
      setShowCreateModal(false)
      setForm({ name: "", permissions: [] })
      void fetchRoles()
    } catch {
      toast.error("Failed to create role")
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingRole) return
    setSaving(true)
    try {
      const res = await client.api.admin.roles[":id"].$patch({
        param: { id: editingRole.id },
        json: { name: form.name, permissions: form.permissions },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Role updated successfully")
      setShowEditModal(false)
      setEditingRole(null)
      setForm({ name: "", permissions: [] })
      void fetchRoles()
    } catch {
      toast.error("Failed to update role")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (role: RoleWithDates) => {
    const confirmed = await confirm({
      title: "Delete role",
      message: `Delete the "${role.name}" role? This cannot be undone.`,
      confirmLabel: "Delete role",
    })
    if (!confirmed) return
    try {
      const res = await client.api.admin.roles[":id"].$delete({
        param: { id: role.id },
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(getErrorMessage(body))
        return
      }
      toast.success("Role deleted")
      void fetchRoles()
    } catch {
      toast.error("Failed to delete role")
    }
  }

  const openEditModal = (role: RoleWithDates) => {
    setEditingRole(role)
    setForm({ name: role.name, permissions: [...role.permissions] })
    setShowEditModal(true)
  }

  const togglePermission = (permission: string) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter((p) => p !== permission)
        : [...prev.permissions, permission],
    }))
  }

  if (!isSuperAdmin) {
    return (
      <div className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Roles</h1>
        <div className="rounded-[10px] border border-gold-500 bg-white p-8 text-center">
          <p className="text-ink/50">You don't have permission to manage roles.</p>
        </div>
      </div>
    )
  }

  if (loading) {
    return <RolesPageSkeleton />
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink sm:text-3xl">Roles</h1>
          <p className="mt-1 text-sm text-ink/60">Create and manage roles with specific permissions.</p>
        </div>
        <button
          onClick={() => {
            setForm({ name: "", permissions: [] })
            setShowCreateModal(true)
          }}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-forest-950 px-5 text-sm font-medium text-white transition-colors hover:bg-forest-900"
        >
          <MembersIcon className="h-4 w-4" />
          Create Role
        </button>
      </div>

      {error ? (
        <div className="rounded-[10px] border border-red-200 bg-red-50 p-8 text-center">
          <p className="text-red-600">{error}</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {roles.map((role) => (
            <div
              key={role.id}
              className="rounded-[10px] border border-gold-500 bg-white p-5 space-y-4"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-ink">{role.name}</h3>
                  {role.isSuper && (
                    <span className="inline-flex items-center rounded-full bg-forest-950/10 px-2 py-0.5 text-xs font-medium text-forest-950 mt-1">
                      Super Admin
                    </span>
                  )}
                </div>
                {role.isSuper ? (
                  <span className="text-xs font-medium text-ink/40">Protected role</span>
                ) : (
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => openEditModal(role)}
                      className="text-sm font-medium text-forest-700 hover:text-forest-900"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(role)}
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-ink/60 mb-2">Permissions</p>
                <div className="flex flex-wrap gap-1">
                  {role.isSuper ? (
                    <span className="rounded-full bg-forest-950/10 px-2 py-0.5 text-xs text-forest-950">
                      All permissions
                    </span>
                  ) : role.permissions.length === 0 ? (
                    <span className="text-xs text-ink/40">No permissions</span>
                  ) : (
                    role.permissions.slice(0, 4).map((p) => (
                      <span
                        key={p}
                        className="rounded-full bg-gold-500/30 px-2 py-0.5 text-xs text-ink/70"
                      >
                        {PERMISSION_LABELS[p as Permission] ?? p}
                      </span>
                    ))
                  )}
                  {!role.isSuper && role.permissions.length > 4 && (
                    <span className="rounded-full bg-gold-500/30 px-2 py-0.5 text-xs text-ink/70">
                      +{role.permissions.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!error && roles.length > 0 && roles.every((role) => role.isSuper) && (
        <div className="rounded-[10px] border border-gold-500/60 bg-cream-100 p-4 text-sm text-ink/60">
          Super Admin is protected. Create a custom role above to edit its name and permissions.
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create role"
          subtitle="Define a new role with specific permissions."
          maxWidth="lg"
        >
            <form onSubmit={handleCreate} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Role Name</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Content Editor"
                />
              </label>
              <div>
                <span className="text-sm font-medium text-ink">Permissions</span>
                <div className="mt-2">
                  <PermissionForm
                    selectedPermissions={form.permissions}
                    onToggle={togglePermission}
                  />
                </div>
              </div>
              <ModalActions>
                <ModalButton onClick={() => setShowCreateModal(false)} disabled={saving}>Cancel</ModalButton>
                <ModalButton type="submit" variant="primary" disabled={saving}>
                  {saving ? "Creating..." : "Create role"}
                </ModalButton>
              </ModalActions>
            </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && editingRole && (
        <Modal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setEditingRole(null)
          }}
          title="Edit role"
          subtitle="Update the role name and permissions."
          maxWidth="lg"
        >
            <form onSubmit={handleEdit} className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-ink">Role Name</span>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 block w-full rounded-lg border border-gold-500 bg-cream-50 px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:border-forest-700 focus:outline-none"
                  placeholder="e.g., Content Editor"
                />
              </label>
              <div>
                <span className="text-sm font-medium text-ink">Permissions</span>
                <div className="mt-2">
                  <PermissionForm
                    selectedPermissions={form.permissions}
                    onToggle={togglePermission}
                  />
                </div>
              </div>
              <ModalActions>
                <ModalButton
                  onClick={() => {
                    setShowEditModal(false)
                    setEditingRole(null)
                  }}
                  disabled={saving}
                >
                  Cancel
                </ModalButton>
                <ModalButton type="submit" variant="primary" disabled={saving}>
                  {saving ? "Saving..." : "Save changes"}
                </ModalButton>
              </ModalActions>
            </form>
        </Modal>
      )}

      {modal}
    </div>
  )
}
